'use client';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/mousewheel';
import '@/styles/vendor/swiper.css';
import { Mousewheel, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import HIHButton from '../base/HIHButton';
import HIHImage from '../base/HIHImage';
import { RoundedIconButton } from '../base/RoundedIconButton';
import { CONTENT_TYPE_PAGE, URL_PAGE_SOON } from '@/shared/constants';
import { Announcement } from '@/shared/types/announcement';
import Image from 'next/image';
import { ImageMeta } from '@/shared/types/imageMeta';
import { contentTypeToPath } from '@/utils/api-helpers';
import Underline from '../base/Underline';
import { useState } from 'react';
import styles from './EventsTodaySlider.module.scss';
import { ContentType } from '@/shared/types/api/contentType';
import { TodaySlider } from '@/shared/types/contentModule';
import { renderInlineMarkup } from '@/utils/renderInlineMarkup';
import EventsTodaySliderEventSlide from './EventsTodaySliderEventSlide';
import {
  MATOMO_ACTIONS,
  MATOMO_CATEGORIES,
  MATOMO_NAMES,
  trackMatomoEvent,
} from '@/utils/matomo';

export default function EventsTodaySlider({
  events,
  title,
  caption,
  className,
  todayLabel,
}: {
  events?: TodaySlider[];
  title?: string;
  caption?: string;
  className?: string;
  // Server-formatted date (current Berlin day), e.g. "28. August".
  todayLabel: string;
}) {
  const [MouseWheelEnabled, setMouseWheelEnabled] = useState(true);

  const ticketLinkOnClick = () => {
    trackMatomoEvent(
      MATOMO_CATEGORIES.INTERACTIONS,
      MATOMO_ACTIONS.TICKET_CLICK,
      MATOMO_NAMES.TODAY_TICKET_CLICK,
    );
  };

  const locationOnClick = () => {
    trackMatomoEvent(
      MATOMO_CATEGORIES.INTERACTIONS,
      MATOMO_ACTIONS.LOCATION_CLICK,
      MATOMO_NAMES.TODAY_LOCATION_CLICK,
    );
  };

  return (
    <div className={className}>
      <div className={styles.header}>
        {/* Datum flexibel setzen */}

        {title ? (
          renderTodayTitle(title, todayLabel)
        ) : (
          <div className={styles.headerTitle}>
            <h1 className={styles.headerTitleH1}>
              Alle Empfehlungen für <br />
              <Underline>heute</Underline>
            </h1>
            <span>
              {TITLE_DATE_SEPARATOR}
              {todayLabel}
            </span>
          </div>
        )}
        <div className={styles.headerCopy}>
          {caption ??
            'Heute noch nichts vor? Lass dich von unserer Auswahl inspirieren. Ob Konzert, Flohmarkt oder Ausstellung, hier findest du dein nächstes Alltagsabenteuer.'}
        </div>
      </div>
      <Swiper
        slidesPerView={1}
        modules={[Pagination, Navigation, Mousewheel]}
        loop={true}
        pagination={{
          clickable: true,
          el: '.swiper-today-slider-pagination',
        }}
        mousewheel={{
          forceToAxis: true,
        }}
        onLock={() => setMouseWheelEnabled(false)}
        onUnlock={() => setMouseWheelEnabled(true)}
        className={[!MouseWheelEnabled ? 'swiper-no-mousewheel' : undefined]
          .filter(Boolean)
          .join(' ')}
      >
        {events?.map((event, i: number) => {
          switch (event.type) {
            case ContentType.Announcement: {
              return (
                <SwiperSlide key={event.title} className={styles.contentSwiperSlide}>
                  <ContentSlide
                    dynamicImage={event.image}
                    title={event.title}
                    copy={event.message}
                    buttonTitle={event.link?.linkLabel}
                    announcement={event}
                    priority
                  />
                </SwiperSlide>
              );
            }
            case ContentType.Event: {
              return (
                <SwiperSlide key={event._id} className={styles.swiperSlide}>
                  <EventsTodaySliderEventSlide
                    event={event}
                    position={i + 1}
                    locationOnClick={locationOnClick}
                    ticketLinkOnClick={ticketLinkOnClick}
                  />
                </SwiperSlide>
              );
            }
            case ContentType.Closing: {
              return (
                <SwiperSlide key="lastSlide" className={styles.contentSwiperSlide}>
                  <ContentSlide
                    staticImageUrl="/images/today-slider-empty-state.png"
                    title={event.title}
                    copy={event.copy}
                    linkUrl={URL_PAGE_SOON}
                    buttonTitle={event.linkLabel}
                  />
                </SwiperSlide>
              );
            }
          }
        })}

        <SwiperButtons />
      </Swiper>
    </div>
  );
}

// Backend ships the title with a trailing date, e.g.
//   "Alle Empfehlungen für\n<u>heute</u>, den 29. Juli"
// The date part must stay out of the <h1> but keep the heading style, so we
// split it off here and render it as a sibling of the heading. The date from
// the title is discarded along the way: the page renders per request
// (force-dynamic) and formats today's date itself (todayLabel) — independent of
// the backend's day-rollover timing and wording.
const TITLE_DATE_SEPARATOR = ', den ';

function renderTodayTitle(title: string, todayLabel: string) {
  const separatorIndex = title.indexOf(TITLE_DATE_SEPARATOR);

  if (separatorIndex === -1) {
    return <h1 className={styles.headerTitle}>{renderInlineMarkup(title)}</h1>;
  }

  const headingPart = title.slice(0, separatorIndex);

  return (
    <div className={styles.headerTitle}>
      <h1 className={styles.headerTitleH1}>{renderInlineMarkup(headingPart)}</h1>
      <span>
        {TITLE_DATE_SEPARATOR}
        {todayLabel}
      </span>
    </div>
  );
}

const SwiperButtons = () => {
  const swiper = useSwiper();
  return (
    <div className={styles.swiperButtons}>
      <RoundedIconButton
        onClick={() => swiper.slidePrev()}
        src="/icons/icon-arrow-left.svg"
        alt="Vorheriges Item"
      />
      <div className={styles.paginationWrapper}>
        <div
          className={[styles.paginationDots, 'swiper-today-slider-pagination'].join(' ')}
        />
      </div>
      <RoundedIconButton
        onClick={() => swiper.slideNext()}
        src="/icons/icon-arrow-right.svg"
        alt="Next"
      />
    </div>
  );
};

function renderAnnouncementButton(announcement: Announcement) {
  const { link } = announcement;
  if (!link || !link.linkLabel) return null;

  const trackAnnouncementClick = (url: string) => {
    trackMatomoEvent(
      MATOMO_CATEGORIES.INTERACTIONS,
      MATOMO_ACTIONS.ANNOUNCEMENT_CLICK,
      `${MATOMO_NAMES.TODAY_ANNOUNCEMENT_CLICK_PREFIX} - ${url}`,
    );
  };

  // external link has priority over internal link
  if (link.linkExternal && link.linkExternal.href) {
    return (
      <div onClick={() => trackAnnouncementClick(link.linkExternal!.href)}>
        <HIHButton
          link={link.linkExternal.href}
          title={link.linkLabel}
          blank={link.linkExternal.blank}
          className={styles.inlineBlock}
          size="md"
          showArrowAfter
        />
      </div>
    );
  }

  if (link.linkInternal && link.linkInternal.slug && link.linkInternal.type) {
    const pathPrefix =
      link.linkInternal.type === CONTENT_TYPE_PAGE
        ? ''
        : `/${contentTypeToPath(link.linkInternal.type)}`;
    const linkPath = `${pathPrefix}/${link.linkInternal.slug}`;

    return (
      <div onClick={() => trackAnnouncementClick(linkPath)}>
        <HIHButton
          link={linkPath}
          title={link.linkLabel}
          className={styles.inlineBlock}
        />
      </div>
    );
  }

  return null;
}

const ContentSlide = ({
  title,
  copy,
  linkUrl,
  buttonTitle,
  staticImageUrl,
  dynamicImage,
  announcement,
  priority,
}: {
  title: string;
  copy?: string;
  linkUrl?: string;
  buttonTitle?: string;
  staticImageUrl?: string;
  dynamicImage?: ImageMeta;
  announcement?: Announcement;
  priority?: boolean;
}) => {
  return (
    <div className={styles.contentSlideGrid}>
      <div className={styles.contentSlideImageCol}>
        {dynamicImage ? (
          <HIHImage
            image={dynamicImage}
            fill
            sizes="(min-width: 1024px) 33vw, 100vw"
            className={styles.contentSlideImage}
            priority={priority}
          />
        ) : (
          staticImageUrl && (
            <Image
              src={staticImageUrl}
              alt="Last Slide Image"
              fill
              sizes="(min-width: 1024px) 33vw, 100vw"
              className={styles.contentSlideImage}
              priority={priority}
            />
          )
        )}
      </div>
      <div className={styles.contentSlideInfo}>
        <div>
          <h2 className={styles.contentSlideTitle}>{title}</h2>
          {copy && <p className={styles.contentSlideCopy}>{copy}</p>}

          {announcement ? (
            renderAnnouncementButton(announcement)
          ) : (
            <HIHButton
              link={linkUrl ?? '/'}
              title={buttonTitle ?? 'Mehr erfahren'}
              className={styles.inlineBlock}
              size="md"
              showArrowAfter
            />
          )}
        </div>
      </div>
    </div>
  );
};
